// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useRouteMatch } from '@shared/core';
import { createTelemetry, createTelemetryObject } from '@shared/core/analytics/telemetry';
import type { TrackFunctionType, PageFunctionType } from '@shared/core/analytics/telemetry/types';
import { useMemo } from 'react';
import type { CookedProduct } from '../../selectors/ProductListSelector';
import type { AddRegistryItemClickedProps } from '../Catalog/Catalog.telemetry.types';
import type { CashFundProduct } from './types';

export type CashFundContext = 'adminRegistryShop' | 'adminRegistryBuild' | 'adminRegistryManage' | 'publicShop';

const PUBLIC_SHOP = 'shop' as const;
const ADMIN_REGISTRY = 'admin' as const;
const NEW_MARKETING = 'newmarketing' as const;

const CASH_FUND_CONTEXT_CATEGORY: ReadonlyRecord<CashFundContext, string> = {
  adminRegistryShop: 'registry.shop',
  adminRegistryBuild: 'registry.build',
  adminRegistryManage: 'registry.manage',
  publicShop: 'shop',
};

const CASH_FUND_CONTEXT_PAGE_PREFIX: ReadonlyRecord<CashFundContext, string> = {
  adminRegistryShop: 'admin',
  adminRegistryBuild: ADMIN_REGISTRY,
  adminRegistryManage: ADMIN_REGISTRY,
  publicShop: NEW_MARKETING,
};

const ADMIN_ROUTE_CASH_FUND_CONTEXT: ReadonlyRecord<string, CashFundContext> = {
  build: 'adminRegistryBuild',
  shop: 'adminRegistryShop',
  manage: 'adminRegistryManage',
};

const CASH_FUND_CONTEXT_PAGE: ReadonlyRecord<CashFundContext, string> = {
  adminRegistryShop: 'shop',
  adminRegistryBuild: 'build',
  adminRegistryManage: 'manage',
  publicShop: 'shop',
};

const INTERNATIONAL_ROUTE_SEGMENT = 'intl';

const INTERNATIONAL_ROUTE_CASH_FUND_CONTEXT: ReadonlyRecord<string, CashFundContext> = {
  'add-gifts': 'adminRegistryShop',
  manage: 'adminRegistryManage',
};

const resolveAdminUsageContext = (page: string, subPage: Maybe<string>): CashFundContext => {
  if (page === INTERNATIONAL_ROUTE_SEGMENT) {
    return INTERNATIONAL_ROUTE_CASH_FUND_CONTEXT[subPage ?? ''] ?? 'adminRegistryShop';
  }
  return ADMIN_ROUTE_CASH_FUND_CONTEXT[page] ?? 'adminRegistryBuild';
};

const addCashFundDialogStepToLabel: ReadonlyRecord<number, string> = {
  0: 'title',
  1: 'goal',
  2: 'paymentMethod',
  3: 'description',
  4: 'discard',
  5: 'success',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wrapFnWithOverrides = <T extends TrackFunctionType | PageFunctionType>(fn: T) => {
  return (...args: Parameters<T>) => {
    let value = fn(...args) as UnsealedReadonly<ReturnType<T>>;

    const overrideArg = args[args.length - 1];

    if (typeof overrideArg === 'object' && overrideArg.__IS_OVERRIDE__) {
      const { __IS_OVERRIDE__, page, ...overrides } = overrideArg;
      value = { ...value, ...overrides };
      if (!value.page && page) {
        value.page = page;
      }
    }
    return value as ReturnType<T>;
  };
};

const telemetryObject = createTelemetryObject({
  pages: {
    cashFundDialogDetails: wrapFnWithOverrides((step: number) => ({
      category: PUBLIC_SHOP,
      pagePrefix: NEW_MARKETING,
      page: `customCashFundDialog.${addCashFundDialogStepToLabel[step]}`,
      action: 'ViewCustomCashFundDialog',
    })),
    honeymoonFundSuccessView: wrapFnWithOverrides(() => ({
      category: PUBLIC_SHOP,
      pagePrefix: NEW_MARKETING,
      page: 'honeymoonFundSuccess',
    })),
  },

  actions: {
    onChangeCashFundDialogState: wrapFnWithOverrides((step: number) => ({
      action: 'CustomCashFundDialogNav',
      actionType: 'click',
      category: PUBLIC_SHOP,
      label: addCashFundDialogStepToLabel[step],
      page: 'shop',
    })),
    onCashFundDialogFieldEdit: wrapFnWithOverrides((field: string, step: number) => ({
      action: 'EditCustomCashFundField',
      actionType: 'click',
      category: PUBLIC_SHOP,
      label: field,
      extraInfo: {
        step: addCashFundDialogStepToLabel[step],
      },
    })),
    onSelectDefaultCoverImage: wrapFnWithOverrides((imageUrl: string, step: number) => ({
      action: 'SelectDefaultCashFundCoverImage',
      actionType: 'click',
      category: PUBLIC_SHOP,
      // Filename identifies the stock pattern (e.g. cash_fund_tropical1.jpg) for NREG-3006.
      label: imageUrl.split('/').pop() ?? imageUrl,
      extraInfo: {
        image: imageUrl,
        step: addCashFundDialogStepToLabel[step],
      },
    })),
    onAddToRegistryClick: wrapFnWithOverrides(({ method, ...restProps }: AddRegistryItemClickedProps) => {
      return {
        action: 'AddRegistryItem',
        actionType: 'click',
        category: PUBLIC_SHOP,
        label: method,
        page: restProps.page,
        extraInfo: {
          method,
          ...restProps,
        },
      };
    }),
    onSaveRegistryItem: wrapFnWithOverrides((product: CashFundProduct | CookedProduct) => ({
      action: 'saveRegistryItem',
      actionType: 'click',
      category: PUBLIC_SHOP,
      extraInfo: {
        product: product,
      },
    })),
    onChangeCurrencyCtaClicked: wrapFnWithOverrides(() => ({
      action: 'ChangeCurrencyCtaClicked',
      actionType: 'click',
      category: PUBLIC_SHOP,
      page: 'shop',
    })),
    onCreateNewFundFromCurrencyMismatchClicked: wrapFnWithOverrides((fundCurrencyCode: string, registryCurrencyCode: string) => ({
      action: 'CreateNewFundFromCurrencyMismatchClicked',
      actionType: 'click',
      category: PUBLIC_SHOP,
      extraInfo: {
        fundCurrencyCode,
        registryCurrencyCode,
      },
    })),
    onSwitchRegistryCurrencyToPayoutClicked: wrapFnWithOverrides((fromCurrencyCode: string, toCurrencyCode: string) => ({
      action: 'SwitchRegistryCurrencyToPayoutClicked',
      actionType: 'click',
      category: PUBLIC_SHOP,
      extraInfo: {
        fromCurrencyCode,
        toCurrencyCode,
      },
    })),
    onHoneymoonRemindMeLater: wrapFnWithOverrides(
      ({
        fundName,
        productId,
        selectedCategoryId,
        isNewCashFund,
      }: {
        fundName: string;
        productId: Maybe<string>;
        selectedCategoryId: Maybe<string>;
        isNewCashFund: Maybe<boolean>;
      }) => ({
        action: 'TravelButtonInteracted',
        category: PUBLIC_SHOP,
        label: 'remindMeLater',
        extraInfo: {
          source: 'Post cash fund add honeymoon pitch',
          fundName,
          productId,
          selectedCategoryId,
          isNewCashFund,
        },
      }),
    ),
    onHoneymoonStartExploring: wrapFnWithOverrides(
      ({
        fundName,
        productId,
        selectedCategoryId,
        isNewCashFund,
      }: {
        fundName: string;
        productId: Maybe<string>;
        selectedCategoryId: Maybe<string>;
        isNewCashFund: Maybe<boolean>;
      }) => ({
        action: 'TravelButtonInteracted',
        category: PUBLIC_SHOP,
        label: 'startExploring',
        extraInfo: {
          source: 'Post cash fund add honeymoon pitch',
          name: 'startExploring',
          fundName,
          productId,
          selectedCategoryId,
          isNewCashFund,
        },
      }),
    ),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(telemetryObject, { eventId: '' }, 'AddCashFundTelemetry');

const useAddCashFundTelemetry = (eventId: string, categoryOverride: Maybe<string>) => {
  const adminRegistryRouteMatch = useRouteMatch<{ eventHandle: string; page: string; subPage?: string }>('/:eventHandle/edit/registry/:page/:subPage?');

  let usageContext: CashFundContext;
  if (adminRegistryRouteMatch) {
    const { page = 'build', subPage } = adminRegistryRouteMatch.params;
    usageContext = resolveAdminUsageContext(page, subPage);
  } else {
    usageContext = 'publicShop';
  }

  const category = categoryOverride ? categoryOverride : CASH_FUND_CONTEXT_CATEGORY[usageContext];
  const pagePrefix = CASH_FUND_CONTEXT_PAGE_PREFIX[usageContext];
  const pageOverride = CASH_FUND_CONTEXT_PAGE[usageContext];
  const telem = useTelemetry({ addToContext: { eventId } });

  return useMemo(() => {
    const { updateContext, context, ...analyticFnsMap } = telem;
    const updatedTelemObject = { updateContext, context } as typeof analyticFnsMap & { updateContext: typeof updateContext; context: typeof context };

    const overrideArgs = { __IS_OVERRIDE__: true, pagePrefix, category, page: pageOverride };

    Object.keys(analyticFnsMap).forEach(key => {
      if (typeof analyticFnsMap[key] === 'object') {
        updatedTelemObject[key] = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedTelemObject[key]['enter'] = (...args: any[]) => analyticFnsMap[key]['enter'](...args, overrideArgs);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedTelemObject[key]['exit'] = (...args: any[]) => analyticFnsMap[key]['exit'](...args, overrideArgs);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedTelemObject[key] = (...args: any[]) => analyticFnsMap[key](...args, overrideArgs);
      }
    });
    return updatedTelemObject;
  }, [category, pageOverride, pagePrefix, telem]);
};

export { TelemetryProvider as AddCashFundTelemetryProvider, useAddCashFundTelemetry };
